import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { EagleLogo } from "@/components/EagleLogo";
import { Printer, Check, Clock, LinkIcon } from "lucide-react";

export default function SafetyMeetingReport() {
    const { meetingId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/safety/meetings/${meetingId}/acks`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e?.response?.data?.detail || "Could not load report."));
    }, [meetingId]);

    if (err) return <div className="p-8 text-center text-brand-danger">{err}</div>;
    if (!data) return <div className="p-8 text-center text-brand-charcoal/60">Loading…</div>;

    const m = data.meeting;
    const pct = data.total ? Math.round((data.watched_count / data.total) * 100) : 0;

    return (
        <div className="paper-bg pb-28 min-h-screen" data-testid="safety-meeting-report">
            <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
                <div className="flex items-center justify-between no-print">
                    <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-brand-admin-header hover:bg-[#6E1824] text-brand-bone font-bold text-sm" data-testid="print-report-button">
                        <Printer className="h-4 w-4" /> Print / Save as PDF
                    </button>
                </div>

                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-6 print:shadow-none print:border-black">
                    <div className="flex items-center gap-3 pb-4 border-b border-brand-charcoal/15">
                        <EagleLogo className="h-10 w-10" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-charcoal/50">Talon Construction Company — Safety Compliance Record</div>
                            <div className="font-bold text-xl">{m.title}</div>
                        </div>
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-4">
                        <dt className="text-brand-charcoal/50">Posted</dt>
                        <dd className="text-brand-charcoal font-semibold">{dayjs(m.created_at).format("MMMM D, YYYY, h:mm A")}</dd>
                        <dt className="text-brand-charcoal/50">Posted by</dt>
                        <dd className="text-brand-charcoal font-semibold">{m.created_by_name || "—"}</dd>
                        <dt className="text-brand-charcoal/50">Format</dt>
                        <dd className="text-brand-charcoal font-semibold">{m.source_type === "video" ? "Video (MP4)" : "Canva presentation"}</dd>
                    </dl>

                    {(m.source_url || m.source_note) && (
                        <div className="mt-4 bg-brand-stone/60 rounded-xl p-4" data-testid="source-citation">
                            <div className="text-[10px] uppercase tracking-widest text-brand-admin-header font-bold mb-1 flex items-center gap-1">
                                <LinkIcon className="h-3.5 w-3.5" /> Content source
                            </div>
                            {m.source_note && <p className="text-sm text-brand-charcoal">{m.source_note}</p>}
                            {m.source_url && (
                                <a href={m.source_url} target="_blank" rel="noreferrer" className="text-sm text-brand-navy underline underline-offset-2 break-all">
                                    {m.source_url}
                                </a>
                            )}
                        </div>
                    )}

                    <div className="mt-5 bg-brand-stone rounded-xl p-4" data-testid="report-summary">
                        <div className="flex items-end justify-between">
                            <span className="text-sm font-bold text-brand-charcoal">Compliance</span>
                            <span className="font-bold text-2xl text-brand-success">{data.watched_count}<span className="text-brand-charcoal/50 text-base"> / {data.total} ({pct}%)</span></span>
                        </div>
                        <div className="mt-2 h-2.5 rounded-full bg-brand-charcoal/10 overflow-hidden">
                            <div className="h-full bg-brand-success" style={{ width: `${pct}%` }} />
                        </div>
                    </div>

                    <div className="mt-5">
                        <div className="text-[10px] uppercase tracking-widest text-brand-success mb-2 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Watched ({data.watched.length})</div>
                        <ul className="space-y-1" data-testid="report-watched-list">
                            {data.watched.length === 0 && <li className="text-sm text-brand-charcoal/50">No one yet.</li>}
                            {data.watched.map((w) => (
                                <li key={w.employee_id} className="flex justify-between text-sm bg-brand-success/10 rounded-lg px-3 py-2 print:bg-transparent print:border-b print:border-black/10 print:rounded-none">
                                    <span className="font-medium text-brand-charcoal">{w.name}</span>
                                    <span className="text-brand-charcoal/50">{dayjs(w.watched_at).format("MMM D, YYYY, h:mm A")}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-5">
                        <div className="text-[10px] uppercase tracking-widest text-brand-danger mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Not watched ({data.not_watched.length})</div>
                        <ul className="space-y-1" data-testid="report-notwatched-list">
                            {data.not_watched.length === 0 && <li className="text-sm text-brand-success font-semibold">Everyone is covered. 🦅</li>}
                            {data.not_watched.map((w) => (
                                <li key={w.employee_id} className="text-sm bg-brand-danger/10 rounded-lg px-3 py-2 font-medium text-brand-charcoal print:bg-transparent print:border-b print:border-black/10 print:rounded-none">{w.name}</li>
                            ))}
                        </ul>
                    </div>

                    <p className="mt-6 pt-4 border-t border-brand-charcoal/10 text-[10px] text-brand-charcoal/40">
                        Generated {dayjs().format("MMMM D, YYYY, h:mm A")} · The Nest — Talon Construction Company
                    </p>
                </div>
            </div>
        </div>
    );
}
